import { padNumeric } from "./stringUtils";

export type DurationCapOptions = {
	maxDisplayHours?: number;
	capLabel?: string;
};

const DEFAULT_MAX_DISPLAY_HOURS = 99;
const DEFAULT_CAP_LABEL = "99:59+";

/** `HH:MM` with two-digit hour and minute parts. */
export function formatHhMm(hours: number, minutes: number): string {
	return `${padNumeric(hours)}:${padNumeric(minutes)}`;
}

/** `HHh MMm` with two-digit hour and minute parts. */
export function formatHoursMinutesDuration(hours: number, minutes: number): string {
	return `${padNumeric(hours)}h ${padNumeric(minutes)}m`;
}

/** `Xd HHh MMm` when days > 0, otherwise `HHh MMm`. */
export function formatDaysHoursMinutesDuration(days: number, hours: number, minutes: number): string {
	if (days > 0) {
		return `${days}d ${padNumeric(hours)}h ${padNumeric(minutes)}m`;
	}
	return formatHoursMinutesDuration(hours, minutes);
}

export function breakdownSecondsToDaysHoursMinutes(totalSeconds: number): {
	days: number;
	hours: number;
	minutes: number;
	totalMinutes: number;
	totalHours: number;
} {
	const totalMinutes = Math.floor(totalSeconds / 60);
	const totalHours = Math.floor(totalMinutes / 60);
	const days = Math.floor(totalHours / 24);
	const hours = totalHours % 24;
	const minutes = totalMinutes % 60;
	return { days, hours, minutes, totalMinutes, totalHours };
}

export function formatDaysHoursMinutesFromTotalSeconds(totalSeconds: number): string {
	const { days, hours, minutes } = breakdownSecondsToDaysHoursMinutes(totalSeconds);
	return formatDaysHoursMinutesDuration(days, hours, minutes);
}

/** Absolute seconds → `HH:MM`, capped when hours exceed `maxDisplayHours`. */
export function formatHhMmFromTotalSeconds(absSeconds: number, options: DurationCapOptions = {}): string {
	const maxDisplayHours = options.maxDisplayHours ?? DEFAULT_MAX_DISPLAY_HOURS;
	const capLabel = options.capLabel ?? DEFAULT_CAP_LABEL;
	const totalMinutes = Math.floor(absSeconds / 60);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	if (hours > maxDisplayHours) {
		return capLabel;
	}
	return formatHhMm(hours, minutes);
}

/**
 * ISO instant → `YYYY-MM-DDTHH:MM` in the browser timezone, for `<input type="datetime-local">`.
 * The input has no timezone, so the value must be the local wall clock of the instant.
 */
export function toDateTimeLocalInputValue(isoDateTime?: string | null): string {
	if (isoDateTime == null || isoDateTime === "") {
		return "";
	}
	const instant = new Date(isoDateTime);
	if (Number.isFinite(instant.getTime()) === false) {
		return "";
	}
	const date = `${instant.getFullYear()}-${padNumeric(instant.getMonth() + 1)}-${padNumeric(instant.getDate())}`;
	return `${date}T${formatHhMm(instant.getHours(), instant.getMinutes())}`;
}

/**
 * `<input type="datetime-local">` value → `YYYY-MM-DD HH:MM:SS` in UTC, for APIs storing UTC timestamps.
 * Returns `null` when the value is empty or not a valid date.
 */
export function toUtcDateTimeString(dateTimeLocalValue: string): string | null {
	const instant = new Date(dateTimeLocalValue);
	if (Number.isFinite(instant.getTime()) === false) {
		return null;
	}
	return instant.toISOString().slice(0, 19).replace("T", " ");
}

/** Whole hours from seconds → `HH:00`, capped when hours exceed `maxDisplayHours`. */
export function formatHoursOnlyHhMmFromTotalSeconds(seconds: number, options: DurationCapOptions = {}): string {
	const maxDisplayHours = options.maxDisplayHours ?? DEFAULT_MAX_DISPLAY_HOURS;
	const capLabel = options.capLabel ?? DEFAULT_CAP_LABEL;
	const hours = Math.floor(seconds / 3600);
	if (hours > maxDisplayHours) {
		return capLabel;
	}
	return `${padNumeric(hours)}:00`;
}

export function getDateFormat(locale: string): string | undefined {
	if (locale === "en") return "en-GB";
	if (locale === "de") return "de-DE";
	if (locale === "es") return "es-ES";
	if (locale === "fr") return "fr-FR";
	if (locale === "it") return "it-IT";
	if (locale === "pt") return "pt-PT";
	return undefined;
}

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Formats a date string for UI. Calendar-only values (`YYYY-MM-DD`, e.g. portal game list API)
 * are formatted in UTC so the calendar day matches the backend everywhere (avoids US vs Europe off-by-one).
 * Date-time strings fall back to the environment's local timezone.
 */
export function formatDateForLocaleDisplay(
	locale: string,
	dateInput: string | null | undefined,
	options: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric" },
): string {
	if (dateInput === null || dateInput === undefined) {
		return "";
	}
	const trimmed = String(dateInput).trim();
	if (trimmed === "") {
		return "";
	}
	const intlLocale = getDateFormat(locale) ?? locale;

	if (ISO_DATE_ONLY.test(trimmed)) {
		const parsed = new Date(`${trimmed}T00:00:00.000Z`);
		if (Number.isNaN(parsed.getTime())) {
			return "";
		}
		return formatLocaleDateWithDotSeparator(
			intlLocale,
			parsed,
			{ ...options, timeZone: "UTC" },
		);
	}

	const parsed = new Date(trimmed);
	if (Number.isNaN(parsed.getTime())) {
		return "";
	}
	return formatLocaleDateWithDotSeparator(intlLocale, parsed, options);
}

/** Locale numeric dates use `/` in most locales; normalize to `DD.MM.YYYY`. */
function formatLocaleDateWithDotSeparator(
	intlLocale: string,
	date: Date,
	options: Intl.DateTimeFormatOptions,
): string {
	return new Intl.DateTimeFormat(intlLocale, options).format(date).replaceAll("/", ".");
}
